import Data from 'data';
import { isSubType } from 'logic/itemTypes';
import { d2_addpercent, d2_percent, skillAuraStats, skillFormula, skillPassiveStats } from 'logic/skills/calc';
import { missileDamage, DamageCalculator } from 'logic/skills/damage';
import { AnimationCalculator, basicAttackCalculator } from '../animation';
import { DPSCalculator, missileDPS } from '../dps';

const functions = {};

// Default skills (projectiles/passives)

functions[0] = {
  aura(id, stats, level) {
    const skill = Data.skills[id];
    if (!skill.passivestate) return;
    return {
      level,
      state: skill.passivestate,
      itemtype: skill.passiveitype,
      stats: () => skillPassiveStats(skill, stats, level),
      priority: 10,
    };
  },
};

// Basic attack

function attackMissile(weapon) {
  const item = Data.items[weapon.baseitem];
  if (!item || !isSubType(item.type, 'miss')) return null;
  if (weapon.item_explosivearrow) {
    return [Data.missileByName.explodingarrow, weapon.item_explosivearrow];
  } else if (weapon.item_magicarrow) {
    return [Data.missileByName.magicarrow, weapon.item_magicarrow];
  } else if (isSubType(weapon, 'xbow')) {
    return [Data.missileByName.bolt, 1];
  } else {
    return [Data.missileByName.arrow, 1];
  }
}

functions[1] = {
  damage(skill, stats, weapon, level, target) {
    const missile = attackMissile(weapon);
    if (missile) {
      return missileDamage(missile[0], stats, weapon, missile[1], target);
    } else {
      const result = new DamageCalculator(stats, weapon, false, true);
      result.finisher();
      result.srcdam(128);
      return result.calculate(target);
    }  
  },
  animation(skill, stats) {
    return basicAttackCalculator(stats, skill)?.calculate();
  },
  dps(id, stats, level, target, info) {
    if (!info?.damage || !info?.animation) return;
    const missile = attackMissile(stats.weapon1);
    if (missile) {
      return missileDPS(Data.skills[id], missile[0], stats, missile[1], target, info);
    } else if (this.damage && info.damage) {
      const calc = new DPSCalculator();
      calc.basic_damage('damage', info.damage.damage, info.animation);
      if (info.damage.offhand) {
        calc.basic_damage('offhand', info.damage.offhand, info.animation);
        calc.entries.damage.interval *= 2;
        calc.entries.offhand.interval *= 2;
      }
      return calc.calculate(stats, target);
    }
  },
};

// Attack skills

functions[2] = {
  showActive: id => !!Data.skills[id].aurastate,
  active: {
    active: false,
  },
  aura(id, stats, level, active) {
    if (!(active || this.active).active) return;
    const skill = Data.skills[id];
    if (!skill.aurastate) return;
    return {
      level,
      state: skill.aurastate,
      stats: () => skillAuraStats(skill, stats, level),
    };
  },
  damage(skill, stats, weapon, level, target) {
    if (!weapon) return null;
    const calc = new DamageCalculator(stats, weapon, false, true);
    if (skill.etype) {
      calc.elemental(skill.etype, skillFormula(skill, 'enms', stats, level),
        skillFormula(skill, 'exms', stats, level), skillFormula(skill, 'edln', stats, level));
    }
    if (skill.srvstfunc !== 35) {
      calc.skillbonus('damagepercent', skillFormula(skill, 'clc1', stats, level));
      calc.convert(skill.etype, skillFormula(skill, 'clc4', stats, level));
    }
    if (skill.kick) {
      calc.kickdamage();
    } else {
      calc.srcdam(skill.srcdam);
    }
    if (skill.srvstfunc === 35) {
      const min = (weapon.mindamage || 0) << 8;
      const max = (weapon.maxdamage || 0) << 8;
      const fire = skillFormula(skill, 'clc1', stats, level);
      const cold = skillFormula(skill, 'clc2', stats, level);
      const ltng = skillFormula(skill, 'clc3', stats, level);
      const fire_mastery = stats.base.passive_fire_mastery || 0;
      const cold_mastery = stats.base.passive_cold_mastery || 0;
      const ltng_mastery = stats.base.passive_ltng_mastery || 0;
      calc.skillbonus('firemindam', d2_addpercent(d2_percent(min, fire), fire_mastery));
      calc.skillbonus('firemaxdam', d2_addpercent(d2_percent(max, fire), fire_mastery));
      calc.skillbonus('coldmindam', d2_addpercent(d2_percent(min, cold), cold_mastery));
      calc.skillbonus('coldmaxdam', d2_addpercent(d2_percent(max, cold), cold_mastery));
      calc.skillbonus('coldlength', skillFormula(skill, 'elen', stats, level));
      calc.skillbonus('lightmindam', d2_addpercent(d2_percent(min, ltng), ltng_mastery));
      calc.skillbonus('lightmaxdam', d2_addpercent(d2_percent(max, ltng), ltng_mastery));
    }
    if (skill.srvstfunc !== 6) {
      calc.skillbonus('tohit_percent', skillFormula(skill, 'toht', stats, level));
    }
    if (skill.srvstfunc > 7) {
      calc.resultflags = skill.resultflags;
    }
    return calc.calculate(target);
  },
  animation(skill, stats, level) {
    const anim = new AnimationCalculator(stats, skill);
    if (!anim.type) return;
    if (skill.srvstfunc !== 35) {
      anim.baseaccel += skillFormula(skill, 'clc3', stats, level);
    }
    return anim.calculate();
  },
  dps(id, stats, level, target, info) {
    if (!info?.damage || !info?.animation) return;
    const skill = Data.skills[id];
    const calc = new DPSCalculator();
    calc.basic_damage('damage', info.damage.damage, info.animation, {specialAttack: skill.psrvstfunc === 7});
    return calc.calculate(stats, target);
  },
};

// Throw

functions[3] = {
  damage(skill, stats, weapon, level, target) {
    const item = Data.items[weapon.baseitem];
    if (!item?.missiletype) return;
    return missileDamage(Data.missiles[item.missiletype], stats, weapon, 1, target);
  },
};

// Left Hand Throw

functions[5] = {
  damage(skill, stats, weapon, level, target) {
    const item = Data.items[weapon.baseitem];
    if (!item?.missiletype) return;
    return missileDamage(Data.missiles[item.missiletype], stats, weapon, 1, target);
  },
};

// Amazon debuffs (Inner Sight/Slow Missiles)

functions[6] = {
  buff(id, stats, level) {
    const skill = Data.skills[id];
    return {
      level,
      filter: skill.aurafilter,
      targetState: skill.auratargetstate,
      targetStats: () => skillAuraStats(skill, stats, level),
    };
  },
};

// Jab

functions[7] = {
  damage(skill, stats, weapon, level, target) {
    if (!weapon) return null;
    const calc = new DamageCalculator(stats, weapon, false, true);
    if (skill.etype) {
      calc.elemental(skill.etype, skillFormula(skill, 'enms', stats, level),
        skillFormula(skill, 'exms', stats, level), skillFormula(skill, 'edln', stats, level));
    }
    calc.skillbonus('damagepercent', skillFormula(skill, 'clc1', stats, level));
    calc.convert(skill.etype, skillFormula(skill, 'clc4', stats, level));
    calc.srcdam(skill.srcdam);
    calc.skillbonus('tohit_percent', skillFormula(skill, 'toht', stats, level));
    return calc.calculate(target);
  },
};

// Multiple Shot, Teeth, Shock Wave

functions[8] = {
  damage(skill, stats, weapon, level, target) {
    const item = Data.items[weapon.baseitem];
    const missile = (item?.wclass === 'xbw' && Data.missileByName[skill.srvmissileb]) || Data.missileByName[skill.srvmissilea];
    if (!missile) return;
    return missileDamage(missile, stats, stats.weapon1, level, target, {
      stats: {
        damagepercent: skillFormula(skill, 'clc4', stats, level),
      },
    });
  },
};

// Frenzy

functions[9] = {
  showActive: id => !!Data.skills[id].aurastate,
  active: {
    stacks: 0,
  },
  aura(id, stats, level, active) {
    level = Math.min((active || this.active).stacks || 0, level);
    if (!level) return;
    const skill = Data.skills[id];
    return {
      level,
      state: skill.aurastate,
      stats: () => skillAuraStats(skill, stats, level),
    };
  },
  damage(skill, stats, weapon, level, target) {
    if (!weapon) return null;
    const calc = new DamageCalculator(stats, weapon, false, true);
    if (skill.etype) {
      calc.elemental(skill.etype, skillFormula(skill, 'enms', stats, level),
        skillFormula(skill, 'exms', stats, level), skillFormula(skill, 'edln', stats, level));
    }
    calc.skillbonus('damagepercent', skillFormula(skill, 'clc1', stats, level));
    calc.convert(skill.etype, skillFormula(skill, 'clc4', stats, level));
    calc.srcdam(skill.srcdam);
    calc.skillbonus('tohit_percent', skillFormula(skill, 'toht', stats, level));
    calc.resultflags = skill.resultflags;
    return calc.calculate(target);
  },
};

export default functions;
